// === Stitched Story Card Swatches v2 ===
// Renders color-circle swatches on collection product cards.
// Each card pulls variants from /products/{handle}.js (cached).
// Click swatch → swap card image (if variant has one) + update card links with ?variant=ID.
(function(){
  'use strict';

  // Color name → hex. Extend as new colors appear.
  const SWATCH_MAP = {
    // Basic
    'blue': '#A8C8E8',
    'green': '#B5D4B8',
    'pink': '#F4B6C2',
    'beige': '#E8D5B7',
    'white': '#FFFFFF',
    'black': '#222222',
    'cream': '#F5EDDC',
    'coral': '#F08A7C',
    'navy': '#2C3E50',
    // Extended
    'mint green': '#B8E6D0',
    'blush pink': '#F2C4CE',
    'sky blue': '#A8D0E6',
    'lavender': '#D4C2E8',
    'dusty rose': '#D4A5A5',
    'sage': '#A8B89E',
    'sage green': '#A8B89E',
    'caramel': '#C68642',
    'ivory': '#F5F0E8',
    'mustard': '#E3A857',
    'rust': '#B5451B',
    'teal': '#5F9EA0',
    'lilac': '#C8A8D4',
    // Denim washes
    'light wash': '#B8C8D8',
    'medium wash': '#7A96AC',
    'dark wash': '#3A5068',
    'light blue': '#A8C8E8',
    'dark blue': '#2C4A6E',
  };

  const cache = new Map();

  function colorToHex(name){
    if (!name) return '#cccccc';
    const k = String(name).trim().toLowerCase();
    if (SWATCH_MAP[k]) return SWATCH_MAP[k];
    // try contains
    for (const key in SWATCH_MAP){
      if (k.indexOf(key) > -1) return SWATCH_MAP[key];
    }
    return '#cccccc';
  }

  async function getProduct(handle){
    if (cache.has(handle)) return cache.get(handle);
    try{
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) throw new Error('fetch failed');
      const p = await res.json();
      cache.set(handle, p);
      return p;
    }catch(e){
      cache.set(handle, null);
      return null;
    }
  }

  function findColorOptionIndex(product){
    if (!product || !product.options) return -1;
    for (let i = 0; i < product.options.length; i++){
      // product.options can be string[] or object[] depending on API
      const opt = product.options[i];
      const name = (typeof opt === 'string' ? opt : (opt.name || '')).trim().toLowerCase();
      if (name === 'color' || name === 'colour') return i;
    }
    return -1;
  }

  function updateCardLinks(card, variantId){
    const links = card.querySelectorAll('a[href*="/products/"]');
    links.forEach(a => {
      try{
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('variant', variantId);
        a.setAttribute('href', u.pathname + u.search);
      }catch(e){}
    });
  }

  function setCardImage(card, src){
    if (!src) return;
    const img = card.querySelector('.product-media__image, img');
    if (!img) return;
    let s = src;
    if (s.startsWith('//')) s = 'https:' + s;
    img.setAttribute('src', s);
    img.removeAttribute('srcset');
  }

  function buildSwatches(card, product){
    const idx = findColorOptionIndex(product);
    if (idx < 0) return;
    const optionName = product.options[idx].name;
    const seen = new Map();
    product.variants.forEach(v => {
      const cval = v.options[idx];
      if (!cval || seen.has(cval)) return;
      let imgSrc = v.featured_image && v.featured_image.src;
      if (!imgSrc) imgSrc = (product.featured_image || '');
      seen.set(cval, {variantId: v.id, imgSrc: imgSrc, available: v.available});
    });
    if (seen.size === 0) return;

    let currentVariant = null;
    const link = card.querySelector('a[href*="?variant="]');
    if (link){
      try{
        const u = new URL(link.getAttribute('href'), location.origin);
        currentVariant = u.searchParams.get('variant');
      }catch(e){}
    }

    const wrap = document.createElement('div');
    wrap.className = 'ss-swatches';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', optionName);

    seen.forEach((info, cname) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ss-swatch';
      dot.setAttribute('data-variant-id', info.variantId);
      dot.setAttribute('data-img', info.imgSrc || '');
      dot.setAttribute('aria-label', cname);
      dot.title = cname;
      dot.style.background = colorToHex(cname);
      if (!info.available) dot.classList.add('is-soldout');
      if (currentVariant && String(currentVariant) === String(info.variantId)){
        dot.classList.add('is-selected');
      }
      dot.addEventListener('click', function(e){
        e.preventDefault();
        e.stopPropagation();
        wrap.querySelectorAll('.ss-swatch').forEach(d => d.classList.remove('is-selected'));
        dot.classList.add('is-selected');
        setCardImage(card, info.imgSrc);
        updateCardLinks(card, info.variantId);
      });
      wrap.appendChild(dot);
    });

    const anchor = card.querySelector('product-price')
      || card.querySelector('[ref="productTitleLink"]')
      || card.querySelector('.product-card__content');
    if (anchor && anchor.parentNode){
      anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
    }
  }

  async function processCard(card){
    if (card.dataset.ssSwatched) return;
    card.dataset.ssSwatched = '1';
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    const m = link.getAttribute('href').match(/\/products\/([^?#\/]+)/);
    if (!m) return;
    const handle = m[1];
    const product = await getProduct(handle);
    if (!product) return;
    buildSwatches(card, product);
  }

  function scan(){
    document.querySelectorAll('product-card, .product-card').forEach(processCard);
  }

  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  const mo = new MutationObserver(muts => {
    let needsScan = false;
    muts.forEach(m => m.addedNodes.forEach(n => {
      if (n.nodeType === 1 && (n.matches?.('product-card, .product-card') || n.querySelector?.('product-card, .product-card'))){
        needsScan = true;
      }
    }));
    if (needsScan) scan();
  });
  mo.observe(document.body, {childList: true, subtree: true});
})();
